import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Bottom Sheet für Historie, Regeln, Menü und jede Rückfrage (GDD §34).
 *
 * Das Dasein des Sheets hängt allein an `open` — bewusst ohne
 * `AnimatePresence`, aus demselben Grund, aus dem der Phasenwechsel in
 * `App.tsx` keine benutzt: Wer das Verschwinden an eine JS-Animation koppelt,
 * hat ein Sheet, das nicht verschwindet, sobald die Animation nicht sauber
 * durchläuft. Genau das war hier der Fall — das ausgeblendete Overlay blieb
 * unsichtbar über der Seite liegen und schluckte danach jeden Tap. Das
 * Hereinfahren ist Schmuck und darf ausfallen, das Schließen nicht.
 *
 * Ein Dialog, der `aria-modal` behauptet, muss den Fokus auch halten: sonst
 * tabbt man am Desktop aus dem offenen Sheet in den Screen dahinter. Das
 * betrifft jede Rückfrage der App, denn `ConfirmSheet`, `HistorySheet` und
 * `RulesSheet` bauen alle hierauf auf (GDD §39).
 */
export function Sheet({ open, onClose, title, children }: SheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    dialogRef.current?.focus();

    return () => {
      // Nur zurückgeben, wenn das Element noch im Dokument hängt: der Auslöser
      // kann inzwischen weg sein — etwa der Menüeintrag, der das Sheet geöffnet
      // hat und mit ihm verschwindet.
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    // Der Container selbst ist fokussierbar (`tabIndex={-1}`), steht aber nicht
    // in der Liste: von ihm aus zeigt `Tab` nach vorn, `Shift+Tab` ans Ende.
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end">
      <div className="absolute inset-0 animate-fade-in bg-black/60" onClick={onClose} />
      <div
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex max-h-[85dvh] animate-sheet-in flex-col rounded-t-3xl border-t border-surface-border bg-surface outline-none"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-surface-border px-5 py-4">
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-2 text-fg-muted"
          >
            ✕
          </button>
        </div>
        <div
          className="overflow-y-auto overscroll-contain px-5 py-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
